import { getAccessToken } from "@/lib/storeManager";
import { getGluceraAccessToken } from "@/projects/Glucera/utils/storage";
import { jwtDecode } from "jwt-decode";

/** Blocked pre-auth — axios + callers treat as silent no-op. */
export const AUTH_GATE_BLOCKED = "AUTH_GATE_BLOCKED";

/** Endpoints allowed before the user signs in (login, refresh, password reset). */
const PUBLIC_API_FRAGMENTS = [
  "/auth/",
  "auth/login",
  "auth/logout",
  "auth/token-refresh",
  "auth/staticgooglelogin",
  "forgot-password-request",
  "forgot-password-reset",
  "social-login-config",
] as const;

/** Glucera screens where the user is not signed in yet. */
export function isGluceraPublicAuthRoute(
  pathname: string = window.location.pathname,
): boolean {
  const lower = pathname.toLowerCase().replace(/\/+$/, "");
  if (lower === "" || lower === "/" || lower === "/glucera") return true;
  return (
    lower.includes("onboardinglogin") ||
    lower.includes("onboarding-welcome") ||
    lower.includes("onboarding_login")
  );
}

export function isPublicApiRequest(url?: string): boolean {
  if (!url) return false;
  const lower = decodeURIComponent(url).toLowerCase();
  return PUBLIC_API_FRAGMENTS.some(fragment => lower.includes(fragment));
}

function getAnyAccessToken(): string | null {
  return getAccessToken() ?? getGluceraAccessToken();
}

export function hasValidAccessToken(): boolean {
  const access = getAnyAccessToken();
  if (!access) return false;
  try {
    const { exp } = jwtDecode<{ exp: number }>(access);
    return exp * 1000 > Date.now() + 30_000;
  } catch {
    return false;
  }
}

/**
 * Global gate: block protected APIs on login/welcome (even with stale tokens)
 * and anywhere else without a valid access token.
 */
export function shouldAllowProtectedApiRequest(
  url?: string,
  options?: { skipAuthGate?: boolean },
): boolean {
  if (options?.skipAuthGate) return true;
  if (isPublicApiRequest(url)) return true;
  if (isGluceraPublicAuthRoute()) return false;
  return hasValidAccessToken();
}

/** Gate translations + user-preferences until login with a valid access token. */
export function shouldFetchAuthenticatedBootstrapApis(): boolean {
  return shouldAllowProtectedApiRequest("/bootstrap");
}

export function isAuthGateBlockedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { code?: string; message?: string };
  return (
    err.code === AUTH_GATE_BLOCKED ||
    err.message === "Blocked pre-auth API call"
  );
}

export function createAuthGateBlockedError(): Error {
  const error = new Error("Blocked pre-auth API call");
  (error as Error & { code: string }).code = AUTH_GATE_BLOCKED;
  return error;
}
